import { isRunningStatus, Log } from '@agilelab/plugin-wb-builder-common';
import {
  GenericField,
  WbCard,
  WbCardContent,
  WbDrawer,
  WbDrawerContent,
  WbLogsCard,
} from '@agilelab/plugin-wb-platform';
import { configApiRef, useApi } from '@mainstage/core-plugin-api';
import { Box, makeStyles, Theme, Typography } from '@material-ui/core';
import { useEffect, useMemo, useState } from 'react';
import { panelCatalogApiRef } from '../../api';
import { useReleaseDetailPageContext } from './context/useReleaseDetailPageContext';
import { useDagStatusColor } from './hooks/useDagStatusColor';
import { useStepIcons } from './hooks/useStepIcons';
import {
  extractDagName,
  getErrors,
  getHumanFriendlyAction,
  getParsedResultField,
} from './utils';

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    '& svg': {
      width: '24px',
      height: '24px',
      marginRight: theme.spacing(1),
    },
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: theme.palette.bkg.primary,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    minHeight: 0,
  },
  resultWrapper: {
    position: 'relative',
    height: 'calc(100% - 150px)',
    '& > svg': {
      position: 'absolute',
      bottom: theme.spacing(3),
      left: '50%',
      transform: 'translateX(-50%)',
      width: `300px`,
      height: `300px`,
      fillOpacity: '0.2',
      strokeOpacity: '0.2',
    },
  },
  codeSnippetWrapper: {
    height: '100%',
    '& > div': {
      overflow: 'auto',
      height: '100%',
    },
  },
  row: {
    display: 'flex',
    width: '100%',
    gap: 50,
    flexWrap: 'wrap',
  },
  logCard: {
    flex: 1,
    [theme.breakpoints.down('sm')]: {
      flex: '1 0 300px',
    },
    marginTop: '16px',
    overflow: 'hidden',
  },
}));

export const DeployStepDetailDrawer = () => {
  const dagStatusColors = useDagStatusColor();
  const classes = useStyles();
  const configApi = useApi(configApiRef);
  const stepIcons = useStepIcons();
  const { selectedStep, selectedDeploy, setSelectedStep } =
    useReleaseDetailPageContext();

  const result = useMemo(() => {
    if (selectedStep) {
      const res = selectedStep.result;
      if (res) {
        try {
          return JSON.stringify(JSON.parse(res), null, 2);
        } catch (_) {
          return res;
        }
      }
    }
    return null;
  }, [selectedStep]);

  const errors = getErrors(result);
  const parsedResult = getParsedResultField(result, configApi);

  const [logs, setLogs] = useState<Log[]>([]);
  const panelCatalog = useApi(panelCatalogApiRef);

  useEffect(() => {
    if (selectedDeploy && selectedStep) {
      const loadData = async () => {
        const logsResponse = await panelCatalog.getLogs(
          selectedDeploy.id,
          selectedStep.id,
        );
        setLogs(logsResponse);
      };

      loadData();
    }
  }, [selectedDeploy, selectedStep, panelCatalog]);

  return (
    <WbDrawer
      title="Deploy Step"
      open={!!selectedStep}
      setOpen={() => setSelectedStep(undefined)}
    >
      <WbDrawerContent style={{ display: 'flex', flexDirection: 'column' }}>
        {selectedStep && (
          <>
            <Box className={classes.header}>
              {stepIcons[selectedStep.status]}
              <Typography color="primary" variant="h6">
                {extractDagName(selectedStep)}
              </Typography>
            </Box>

            <WbCard title="Details">
              <WbCardContent>
                <Box className={classes.row}>
                  <GenericField
                    label="Start Time"
                    value={selectedStep.startTime}
                    type="date"
                  />
                  {!isRunningStatus(selectedStep.status) &&
                    selectedStep.stopTime && (
                      <GenericField
                        label="Stop Time"
                        value={selectedStep.stopTime}
                        type="date"
                      />
                    )}
                  <GenericField
                    label="Status"
                    value={selectedStep.status}
                    type="string"
                    contentProps={{
                      style: { color: dagStatusColors[selectedStep.status] },
                    }}
                  />
                  <GenericField
                    label="Action"
                    value={getHumanFriendlyAction(selectedStep.action)}
                    type="string"
                  />
                </Box>
              </WbCardContent>
            </WbCard>
            {result && (
              <WbLogsCard
                title="Result"
                text={errors?.join('\n') || parsedResult || result}
                className={classes.logCard}
                language="go"
              />
            )}
            <WbLogsCard
              title="Log File"
              logs={logs}
              className={classes.logCard}
            />
          </>
        )}
      </WbDrawerContent>
    </WbDrawer>
  );
};
